import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { financingTypes } from '../../data/mock';
import { Home, CreditCard, RefreshCw, Wallet, Briefcase, Banknote, ArrowRight, Calculator } from 'lucide-react';

const iconMap = {
  Home,
  CreditCard,
  RefreshCw,
  Wallet,
  Briefcase,
  Banknote
};

const ComparisonModule = () => {
  const [selectedType, setSelectedType] = useState('kapitalbeschaffung');
  const [amount, setAmount] = useState([100000]);
  const [term, setTerm] = useState([120]);

  const selectedFinancing = financingTypes.find(t => t.id === selectedType);

  const calculateMonthlyRate = () => {
    const principal = amount[0];
    const months = term[0];
    const rate = parseFloat(selectedFinancing?.rates.minRate) / 100 / 12;
    if (rate === 0) return (principal / months).toFixed(2);
    const payment = (principal * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    return payment.toFixed(2);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Finanzierungsrechner</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
            Berechnen Sie Ihre Finanzierung
          </h2>
          <p className="text-slate-600">
            Wählen Sie Ihre Finanzierungsart und berechnen Sie Ihre monatliche Rate.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="bg-slate-50 rounded-3xl p-6 md:p-10 border border-slate-100">
            {/* Financing Type Selection */}
            <div className="mb-10">
              <label className="block text-sm font-medium text-slate-700 mb-4">Finanzierungsart wählen</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {financingTypes.map((type) => {
                  const IconComponent = iconMap[type.icon];
                  return (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type.id)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                        selectedType === type.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-blue-300'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${
                        selectedType === type.id ? 'bg-blue-600' : 'bg-slate-100'
                      }`}>
                        {IconComponent && (
                          <IconComponent className={`w-5 h-5 ${
                            selectedType === type.id ? 'text-white' : 'text-slate-600'
                          }`} />
                        )}
                      </div>
                      <p className={`text-xs font-medium text-center ${
                        selectedType === type.id ? 'text-blue-700' : 'text-slate-600'
                      }`}>
                        {type.title.split(' ')[0]}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-10">
              {/* Sliders */}
              <div className="space-y-8">
                {/* Amount Slider */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="text-sm font-medium text-slate-700">Darlehensbetrag</label>
                    <span className="text-lg font-bold text-blue-600">
                      {amount[0].toLocaleString('de-DE')} €
                    </span>
                  </div>
                  <Slider
                    value={amount}
                    onValueChange={setAmount}
                    min={5000}
                    max={500000}
                    step={5000}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>5.000 €</span>
                    <span>500.000 €</span>
                  </div>
                </div>

                {/* Term Slider */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="text-sm font-medium text-slate-700">Laufzeit</label>
                    <span className="text-lg font-bold text-blue-600">
                      {term[0]} Monate ({Math.floor(term[0] / 12)} Jahre)
                    </span>
                  </div>
                  <Slider
                    value={term}
                    onValueChange={setTerm}
                    min={12}
                    max={360}
                    step={12}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>12 Monate</span>
                    <span>360 Monate</span>
                  </div>
                </div>
              </div>

              {/* Result Card */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedFinancing?.title}</p>
                    <p className="text-sm text-slate-500">ab {selectedFinancing?.rates.minRate} eff. Jahreszins</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Darlehensbetrag</span>
                    <span className="font-semibold text-slate-900">{amount[0].toLocaleString('de-DE')} €</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-slate-100">
                    <span className="text-slate-600">Laufzeit</span>
                    <span className="font-semibold text-slate-900">{term[0]} Monate</span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-slate-600">Zinssatz ab</span>
                    <span className="font-semibold text-blue-600">{selectedFinancing?.rates.minRate}</span>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-slate-600 mb-1">Geschätzte monatliche Rate</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {parseFloat(calculateMonthlyRate()).toLocaleString('de-DE')} €
                    <span className="text-sm font-normal text-slate-500"> / Monat</span>
                  </p>
                </div>

                <Link to="/anfrage">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-md">
                    Unverbindlich anfragen
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>

                <p className="text-xs text-slate-500 text-center mt-4">
                  * Beispielrechnung, tatsächliche Konditionen können abweichen
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonModule;
